import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import TextInput from 'assets/components/inputs/text/TextInput';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateFormat, defaultInputDateFormat, defaultServerDateFormat } from 'assets/utils/data/Date';
import { HttpStatus } from 'config/Api.Config';
import dayjs from 'dayjs';
import claimInvoiceDeleteApi from 'models/claims/claimInvoice/delete/Api.ClaimInvoice.Delete';
import claimInvoiceEditApi from 'models/claims/claimInvoice/edit/Api.ClaimInvoice.Edit';
import claimInvoiceSaveApi from 'models/claims/claimInvoice/save/Api.ClaimInvoice.Save';
import ClaimStatusCode from 'models/enums/ClaimStatusCode';
import { Fragment, useEffect, useMemo, useState } from 'react';
import DocumentPreview from '../../../../../assets/components/other/DocumentPreview';
import { useClaimEdit } from '../ClaimEdit.Hooks';

export default function ClaimEditInvoices() {
  const { lang } = getLocales();
  const { reload, claim, isClaimAdmin, permissions } = useClaimEdit();

  const isClaimInCorrectStatus =
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_IN_PROGRESS ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_CUSTOMER ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_DEALERSHIP ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_OTHER ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_REPAIRFACILITY ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_SELLING_DEALER;

  const docForm = useForm<{
    claimId: number;
    documents: Array<Utils.FormData<Omit<Model.IClaimInvoice, 'claimId'>>>;
  }>({
    default: { claimId: claim?.id, documents: [] },
    onSubmit: async ({ claimId, documents }) => {
      for (const document of documents) {
        if (document.formFile) {
          const res = await claimInvoiceSaveApi({ ...document, claimId });
          if (res.status !== HttpStatus.ok) {
            addLog({ error: lang.uploadError });
          }
        }
      }
      docForm.reload();
      reload();
    },
    validation: (data, errors) => {
      if (data.documents?.some((it) => !it.name)) {
        errors.documents = { name: lang.required };
      }
      if (data.documents?.some((it) => !it.invoiceNumber)) {
        errors.documents = { invoiceNumber: lang.required } as any;
      }
    },
  });

  useEffect(() => {
    if (docForm?.data?.claimId !== claim?.id) {
      docForm.set({ claimId: claim?.id });
    }
  }, [claim?.id]);

  const [editingDoc, setEditingDoc] = useState<{
    id: number;
    name: string;
    notes?: string;
    invoiceNumber?: string;
    invoiceDate?: string;
  } | null>(null);

  const editForm = useForm<Pick<Model.IClaimInvoice, 'name' | 'notes' | 'invoiceNumber' | 'invoiceDate'>>({
    default: { name: '', notes: '', invoiceNumber: '', invoiceDate: '' },
    onSubmit: async ({ name, notes, invoiceNumber, invoiceDate }) => {
      if (!claim?.id || !editingDoc?.id) return;

      const body: Api.Core.ClaimInvoice.Edit.IRequest = {
        id: editingDoc.id,
        claimId: claim.id,
        name,
        notes,
        invoiceNumber,
        invoiceDate,
      } as Api.Core.ClaimInvoice.Edit.IRequest;

      const res = await claimInvoiceEditApi(body);

      if (res.status !== HttpStatus.ok) {
        addLog({ error: lang.uploadError });
      }

      setEditingDoc(null);
      reload();
    },
    validation: (data, errors) => {
      if (!data.name) errors.name = lang.required;
      if (!data.invoiceNumber) errors.invoiceNumber = lang.required;
    },
  });

  useEffect(() => {
    if (editingDoc) {
      const normalizedInvoiceDate = editingDoc.invoiceDate
        ? dayjs(editingDoc.invoiceDate, 'DD/MMM/YYYY').format('YYYY-MM-DDT00:00:00')
        : '';

      editForm.set({
        name: editingDoc.name,
        notes: editingDoc.notes ?? '',
        invoiceNumber: editingDoc.invoiceNumber,
        invoiceDate: normalizedInvoiceDate ?? '',
      });
    }
  }, [editingDoc?.id]);

  const onClaimInvoiceEdit = (doc: {
    id: number;
    name: string;
    description?: string;
    invoiceNumber?: string;
    invoiceDate?: string;
  }) => {
    setEditingDoc({
      id: doc.id,
      name: doc.name,
      notes: doc.description,
      invoiceNumber: doc.invoiceNumber,
      invoiceDate: doc.invoiceDate,
    });
  };

  const onClaimInvoiceDelete = async (invoiceId: number) => {
    await claimInvoiceDeleteApi({ id: claim?.id, invoiceId });
    reload();
  };

  const uploadedFiles = useMemo(
    () =>
      docForm.data?.documents?.map((doc) => ({
        original: doc.formFile,
        type: doc.formFile.type as Utils.File.MimeType,
        dataUrl: window.URL.createObjectURL(doc.formFile),
        name: doc.name,
        invoiceNumber: doc.invoiceNumber,
        invoiceDate: doc.invoiceDate,
        notes: doc.notes,
      })),
    [docForm.data.documents]
  );

  const isNonEditable = !isClaimInCorrectStatus || !(isClaimAdmin || permissions?.UPLOAD_CLAIMINVOICE);
  const isDisabled =
    !isClaimInCorrectStatus ||
    docForm.hasErrors ||
    docForm.isSubmitted ||
    !docForm.isUpdated ||
    !docForm.data.documents?.length;
  const isEditDisabled = !isClaimInCorrectStatus || !(isClaimAdmin || permissions?.EDIT_CLAIMINVOICE);
  const isDeleteDisabled = !isClaimInCorrectStatus || !(isClaimAdmin || permissions?.DELETE_CLAIMINVOICE);

  return (
    <>
      <h2 id="invoices">{lang.invoices}</h2>
      <br />
      <InputWrapper>
        <FileInputSimple
          name="files"
          value={uploadedFiles}
          onChange={({ files }) => {
            docForm.update({
              documents: [
                ...(docForm.data.documents ?? []),
                ...files.map((file) => ({
                  formFile: file.original,
                  name: file.original?.name,
                })),
              ],
            });
          }}
          label={lang.documentUpload}
          disabled={isNonEditable}
        />
        <Button
          render={lang.upload}
          onClick={docForm.submit}
          disabled={isDisabled}
          active={!docForm.hasErrors}
          class={ThemeButtonCircle}
          style={{ flexShrink: 0 }}
        />
      </InputWrapper>
      <DataMap
        data={docForm.data.documents ?? []}
        render={({ data: uploadedFile, index }) => (
          <Fragment key={index}>
            <br />
            <br />
            <InputWrapper>
              <TextInput
                name="name"
                label={lang.name}
                value={uploadedFile.name}
                error={!uploadedFile.name ? lang.required : undefined}
                onChange={(data) => {
                  docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                  docForm.update({ documents: [...docForm.data.documents] });
                }}
                disabled={isNonEditable}
              />
              <TextInput
                name="invoiceNumber"
                label={lang.invoiceNumber}
                value={uploadedFile.invoiceNumber}
                error={!uploadedFile.invoiceNumber ? lang.required : undefined}
                onChange={(data) => {
                  docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                  docForm.update({ documents: [...docForm.data.documents] });
                }}
                disabled={isNonEditable}
              />
              <DateInput
                name="invoiceDate"
                label={lang.invoiceDate}
                value={uploadedFile.invoiceDate}
                onChange={(data) => {
                  docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                  docForm.update({ documents: [...docForm.data.documents] });
                }}
                isLocal
                dateTimeFormat={defaultInputDateFormat}
                disabled={isNonEditable}
              />
              <TextInput
                name="notes"
                label={lang.notes}
                value={uploadedFile.notes}
                onChange={(data) => {
                  docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                  docForm.update({ documents: [...docForm.data.documents] });
                }}
                disabled={isNonEditable}
              />
            </InputWrapper>
          </Fragment>
        )}
      />
      <br />

      <DocumentPreview
        documents={
          claim?.claimInvoices?.map((it) => ({
            id: it.id,
            url: it.mediaFile.url,
            name: it.name,
            extension: it.mediaFile?.extension,
            mimeType: it.mediaFile?.contentType,
            storedFileName: it.mediaFile?.name,
            description: it.notes,
            info: lang.uploaded,
            invoiceNumber: it.invoiceNumber,
            invoiceDate: it.invoiceDate
              ? dayjs(it.invoiceDate, defaultServerDateFormat).format(defaultDateFormat)
              : undefined,
            documentType: lang.invoice,
            onDelete: () => onClaimInvoiceDelete(it.id),
            onEdit: (doc) => onClaimInvoiceEdit(doc),
          })) ?? []
        }
        showGallery={true}
        isDeleteDisabled={isDeleteDisabled}
        isDownloadDisabled={!(isClaimAdmin || permissions?.DOWNLOAD_CLAIMINVOICE)}
        isEditDisabled={isEditDisabled}
      >
        {editingDoc && (
          <div style={{ marginLeft: Theme.Size.XL, marginRight: Theme.Size.XL }}>
            <h3>{`${lang.edit} ${lang.invoices}`}</h3>
            <br />
            <InputWrapper>
              <TextInput
                name="name"
                label={lang.name}
                value={editForm.data.name}
                error={editForm.errors?.name}
                onChange={(data) => editForm.update(data)}
                disabled={editForm.isSubmitted}
              />
              <TextInput
                name="invoiceNumber"
                label={lang.invoiceNumber}
                value={editForm.data.invoiceNumber}
                error={editForm.errors?.invoiceNumber}
                onChange={(data) => editForm.update(data)}
                disabled={editForm.isSubmitted}
              />
              <DateInput
                name="invoiceDate"
                label={lang.invoiceDate}
                value={editForm.data.invoiceDate}
                onChange={(data) => editForm.update(data)}
                isLocal
                dateTimeFormat={defaultInputDateFormat}
                disabled={editForm.isSubmitted}
              />
              <TextInput
                name="notes"
                label={lang.notes}
                value={editForm.data.notes}
                onChange={(data) => editForm.update(data)}
                disabled={editForm.isSubmitted}
              />
              <Button
                render={lang.save}
                onClick={editForm.submit}
                active={!editForm.hasErrors}
                disabled={editForm.hasErrors || editForm.isSubmitted}
                class={ThemeButtonCircle}
                style={{ flexShrink: 0 }}
              />
              <Button
                render={lang.cancel}
                onClick={() => setEditingDoc(null)}
                class={ThemeButtonCircle}
                style={{ flexShrink: 0 }}
              />
            </InputWrapper>
            <br />
            <br />
          </div>
        )}
      </DocumentPreview>
    </>
  );
}
